import { useCallback, useEffect, useRef, useState } from 'react';
import { SAKTI_CONFIG_DEFAULTS, SAKTI_DEFAULTS } from '../apiConfig';
import SaktiConfigApiService from '../services/saktiConfigApiService';
import { extractApiError } from '../utils/apiError';
import {
  SaktiPaginationMeta,
  SaktiTableColumn,
  SaktiTableInfo,
  SaktiTableRow,
} from '../types';

const EMPTY_PAGINATION: SaktiPaginationMeta = {
  page: 1,
  limit: SAKTI_CONFIG_DEFAULTS.DATA_VIEWER_LIMIT,
  total_items: 0,
  total_pages: 0,
};

export type SortDir = 'asc' | 'desc';

const qualifiedName = (table: SaktiTableInfo): string => `${table.name}`;

interface UseSaktiDataViewerResult {
  tables: SaktiTableInfo[];
  loadingTables: boolean;
  tablesError: string | null;
  selectedTable: string | null;
  columns: SaktiTableColumn[];
  rows: SaktiTableRow[];
  pagination: SaktiPaginationMeta;
  loadingRows: boolean;
  rowsError: string | null;
  searchInput: string;
  sortBy: string | null;
  sortDir: SortDir;
  qualifiedName: (table: SaktiTableInfo) => string;
  selectTable: (table: string) => void;
  setSearchInput: (value: string) => void;
  toggleSort: (column: string) => void;
  goToPage: (page: number) => void;
  reloadTables: () => Promise<void>;
}

export const useSaktiDataViewer = (): UseSaktiDataViewerResult => {
  const [tables, setTables] = useState<SaktiTableInfo[]>([]);
  const [loadingTables, setLoadingTables] = useState<boolean>(true);
  const [tablesError, setTablesError] = useState<string | null>(null);

  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [columns, setColumns] = useState<SaktiTableColumn[]>([]);
  const [rows, setRows] = useState<SaktiTableRow[]>([]);
  const [pagination, setPagination] = useState<SaktiPaginationMeta>(EMPTY_PAGINATION);
  const [loadingRows, setLoadingRows] = useState<boolean>(false);
  const [rowsError, setRowsError] = useState<string | null>(null);

  // searchInput is the live box value; appliedSearch is what we actually query with
  // (debounced) — each keystroke would otherwise trigger a full-table ILIKE + COUNT.
  const [searchInput, setSearchInputState] = useState<string>('');
  const [appliedSearch, setAppliedSearch] = useState<string>('');
  const [page, setPage] = useState<number>(SAKTI_DEFAULTS.DEFAULT_PAGE);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const mountedRef = useRef<boolean>(true);
  // Monotonic request id: drop responses that arrive out of order (stale).
  const requestRef = useRef<number>(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const reloadTables = useCallback(async () => {
    setLoadingTables(true);
    setTablesError(null);
    try {
      const response = await SaktiConfigApiService.listTables();
      if (!mountedRef.current) {
        return;
      }
      setTables(response.data);
    } catch (err) {
      if (!mountedRef.current) {
        return;
      }
      setTablesError(extractApiError(err).message);
    } finally {
      if (mountedRef.current) {
        setLoadingTables(false);
      }
    }
  }, []);

  useEffect(() => {
    reloadTables();
  }, [reloadTables]);

  // Debounce the search box into appliedSearch and reset to page 1.
  useEffect(() => {
    const handle = setTimeout(() => {
      setAppliedSearch(searchInput.trim());
      setPage(SAKTI_DEFAULTS.DEFAULT_PAGE);
    }, SAKTI_CONFIG_DEFAULTS.SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [searchInput]);

  // Fetch rows whenever the table, page, applied search, or sort changes.
  useEffect(() => {
    if (!selectedTable) {
      return;
    }

    const requestId = requestRef.current + 1;
    requestRef.current = requestId;

    const fetchRows = async () => {
      setLoadingRows(true);
      setRowsError(null);
      try {
        const response = await SaktiConfigApiService.getTableRows({
          table: selectedTable,
          page,
          limit: SAKTI_CONFIG_DEFAULTS.DATA_VIEWER_LIMIT,
          search: appliedSearch || undefined,
          sortBy: sortBy || undefined,
          sortDir: sortBy ? sortDir : undefined,
        });

        if (!mountedRef.current || requestRef.current !== requestId) {
          return;
        }

        setColumns(response.columns);
        setRows(response.data);
        setPagination(response.pagination);
      } catch (err) {
        if (!mountedRef.current || requestRef.current !== requestId) {
          return;
        }
        setRowsError(extractApiError(err).message);
        setRows([]);
      } finally {
        if (mountedRef.current && requestRef.current === requestId) {
          setLoadingRows(false);
        }
      }
    };

    fetchRows();
  }, [selectedTable, page, appliedSearch, sortBy, sortDir]);

  const selectTable = useCallback((table: string) => {
    setSelectedTable(table);
    setColumns([]);
    setRows([]);
    setPagination(EMPTY_PAGINATION);
    setSearchInputState('');
    setAppliedSearch('');
    setSortBy(null);
    setSortDir('asc');
    setPage(SAKTI_DEFAULTS.DEFAULT_PAGE);
  }, []);

  const setSearchInput = useCallback((value: string) => {
    setSearchInputState(value);
  }, []);

  const toggleSort = useCallback(
    (column: string) => {
      if (sortBy === column) {
        setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(column);
        setSortDir('asc');
      }
      setPage(SAKTI_DEFAULTS.DEFAULT_PAGE);
    },
    [sortBy]
  );

  const goToPage = useCallback((nextPage: number) => {
    setPage(nextPage);
  }, []);

  return {
    tables,
    loadingTables,
    tablesError,
    selectedTable,
    columns,
    rows,
    pagination,
    loadingRows,
    rowsError,
    searchInput,
    sortBy,
    sortDir,
    qualifiedName,
    selectTable,
    setSearchInput,
    toggleSort,
    goToPage,
    reloadTables,
  };
};

export default useSaktiDataViewer;
